import { useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { refusalText } from "../api/errors";
import { useApiCommand } from "../api/hooks";
import { Banner } from "../ui/Banner";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { TextField } from "../ui/TextField";
import { showToast } from "../ui/toastStore";
import { homePath } from "./access";
import { adoptMe, useCurrentMe, useSignOut } from "./auth";
import { PlainFrame } from "./PlainFrame";
import { addressAfterSignIn } from "./SignInPage";

export function ChangePasswordPage() {
  const me = useCurrentMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const signOut = useSignOut();
  const change = useApiCommand("change_password");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [again, setAgain] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  const forced = me.must_change_password;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setProblem(null);
    if (newPassword !== again) {
      setProblem("The two new passwords are not the same.");
      return;
    }
    try {
      const reply = await change.mutateAsync({ old_password: oldPassword || null, new_password: newPassword });
      adoptMe(queryClient, reply.data);
      showToast({ text: "Your password was changed." });
      void navigate(addressAfterSignIn(location.state) ?? homePath(reply.data), { replace: true });
    } catch (error) {
      setProblem(refusalText(error));
      setNewPassword("");
      setAgain("");
    }
  };

  return (
    <PlainFrame>
      <Card aria-labelledby="change-password-title" className="sign-in">
        <h1 id="change-password-title">Change your password</h1>
        {forced && <Banner>You need to choose a new password before you start.</Banner>}
        <form onSubmit={(event) => void submit(event)} className="stack" noValidate>
          <TextField
            label="Your password now"
            name="old_password"
            type="password"
            autoComplete="current-password"
            value={oldPassword}
            onChange={(event) => setOldPassword(event.target.value)}
            hint={forced ? "This is the first password the admin gave you." : undefined}
          />
          <TextField
            label="New password"
            name="new_password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            required
          />
          <TextField
            label="New password again"
            name="new_password_again"
            type="password"
            autoComplete="new-password"
            value={again}
            onChange={(event) => setAgain(event.target.value)}
            required
          />
          {problem && (
            <p role="alert" className="form-error">
              {problem}
            </p>
          )}
          <div className="row">
            <Button type="submit" variant="primary" busy={change.isPending} disabled={!newPassword || !again}>
              Change password
            </Button>
            {forced ? (
              <Button variant="quiet" onClick={() => void signOut()}>
                Sign out
              </Button>
            ) : (
              // Back to where the person came from (My profile, the Admin page), else to the home screen.
              <Link to={addressAfterSignIn(location.state) ?? homePath(me)}>Cancel</Link>
            )}
          </div>
        </form>
      </Card>
    </PlainFrame>
  );
}
